import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { cloneDeep, isEmpty } from 'lodash';
import { Stack } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import type { CSSProperties } from '@mui/styles';
import type { ApiEnumGroup, ApiItemData, ApiMandatoryLevel } from '@agunity/api-v4/build/data-contracts';

import AgTextArea from '../../AgTextArea';
import AgChip from '../../../Chips/AgChip';
import AgTextField from '../../AgTextField';
import AgDropdown from '../../../AgDropdown';
import { AgColor } from '../../../Styles/AgColor';
import AgTypography from '../../../Styles/AgTypography';
import AgDatePicker from '../../CustomDropdowns/AgDatePicker';
import AgSimpleTable, { ProfileSectionTableData } from '../../AgSimpleTable';
import type { HasOnBlurProps } from '../../../../pages/MemberInfo/AddMember/AddMember';

import { useAppSelector } from '../../../../redux/hooks';
import { selectUnits } from '../../../../redux/slices/global';
import { CustomFieldColumnType, MANDATORY_LEVEL, ROOT_PATHS } from '../../../../Constants';

type AgCustomFieldGroupProps = {
  title: string;
  fields: CustomFields[];
  editOnly?: boolean;
  mainWidth?: CSSProperties['width'];
  expandMain?: boolean;
  setExpandMain?: Function;
  // eslint-disable-next-line no-unused-vars
  setData?: (data: ApiItemData[]) => void;
  data?: ApiItemData[];
  // eslint-disable-next-line no-unused-vars
  setOrgData?: (data: ApiItemData[]) => void;
  setPersonalDetails?: Dispatch<SetStateAction<any | undefined>>;
  identity?: any;
  orgData?: ApiItemData[];
  setHasMemberProfileError?: Dispatch<SetStateAction<boolean>>;
  showMemberProfileError?: boolean;
  setHasPersonalDetailsError?: Dispatch<SetStateAction<boolean>>;
  showPersonalDetailsError?: boolean;
  showError?: boolean;
  enumList?: ApiEnumGroup[];
  hasOnBlur?: HasOnBlurProps;
  setHasOnBlur?: Dispatch<SetStateAction<HasOnBlurProps>>;
  // eslint-disable-next-line no-unused-vars
  setErrors?: (error: Record<string, boolean>) => void;
};

export interface CustomFields extends ProfileSectionTableData {
  id?: number;
  name: string;
  value?: string | number;
  error?: boolean;
  caption?: string;
  mandatoryLevel?: ApiMandatoryLevel;
  fieldType?: number;
  maxLength?: number;
  unitId?: number | null;
}

const AgCustomFieldGroup = ({
  title,
  fields,
  editOnly,
  mainWidth,
  expandMain,
  setExpandMain,
  setPersonalDetails,
  identity,
  setData,
  data,
  setOrgData,
  orgData,
  showMemberProfileError,
  showPersonalDetailsError,
  showError,
  enumList,
  hasOnBlur,
  setHasOnBlur,
  setErrors
}: AgCustomFieldGroupProps) => {
  const [expand, setExpand] = useState<boolean>(false);
  const expandTmp = expandMain !== undefined ? expandMain : expand;
  const messages = useIntl().formatMessage;
  const units = useAppSelector(selectUnits);

  const currentPath = window.location.pathname;
  const readOnlyFields = () => {
    return (
      <AgSimpleTable
        tableData={fields.map((field) => {
          let fieldValue: unknown & ReactNode = field.value;
          let fieldName = field.name;
          const unit = field?.unitId && (units || []).find(({ id }) => id === field?.unitId)?.name;
          
          // Enum and MultiEnum fields
          const isEnumField = [CustomFieldColumnType.Enum, CustomFieldColumnType.MultiEnum].includes(field.fieldType!);
          if (isEnumField) {
            const enumType = enumList?.find((item) => item.id === Number(field.name));
            fieldName = enumType?.name || '';
            fieldValue = (
              <Stack direction='row' gap={1} flexWrap='wrap'  sx={{ 'div': { padding: 0 } }}>
                {fieldValue?.toString().split(',').map((name, index) => {
                  return <AgChip key={`${name}-${index}`} label={name} />;
                })}
              </Stack>
            );
          };

          return {
            name: fieldName,
            value: (unit && fieldValue) ? `${fieldValue} ${unit}` : fieldValue
          };
        })}
        style={{
          paddingTop: '16px'
        }}
      />
    );
  };

  const hasConditionalFieldNotEmpty = (fields: CustomFields[]) => {
    let result = false;
    fields.forEach((field) => {
      if (
        field.mandatoryLevel === MANDATORY_LEVEL.Conditional &&
        !isEmpty(field.value)
      ) {
        result = true;
      }
    });
    return result;
  };

  const hasError = useCallback((field: CustomFields, forceValidate = false) => {
    return (
      (((field.mandatoryLevel === MANDATORY_LEVEL.Mandatory &&
        isEmpty(field.value)) || // if field is mandatory and value is empty
        (field.mandatoryLevel === MANDATORY_LEVEL.Conditional &&
          hasConditionalFieldNotEmpty(fields) &&
          isEmpty(field.value))) && // if field is conditional and any other conditional field is not empty
        (showMemberProfileError || showPersonalDetailsError || showError || forceValidate)) ||
      (currentPath === ROOT_PATHS.ADD_MEMBER &&
        hasOnBlur &&
        hasOnBlur[field.name] &&
        field.mandatoryLevel === MANDATORY_LEVEL.Mandatory &&
        isEmpty(field.value)) || // logic for add new member page
      (currentPath === ROOT_PATHS.ADD_MEMBER &&
        hasOnBlur &&
        hasOnBlur[field.name] &&
        hasConditionalFieldNotEmpty(fields) &&
        field.mandatoryLevel === MANDATORY_LEVEL.Conditional &&
        isEmpty(field.value)) // logic for add new member page
    );
  }, [currentPath, fields, hasOnBlur, showError, showMemberProfileError, showPersonalDetailsError]);

  const preventInputLetterE = (event: { key: string; preventDefault: () => void; }) => {
    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  };

  const handleEnumORMultiEnum = useCallback((props: { data: ApiItemData[]; value: string | string[]; field: CustomFields; options: ApiEnumGroup[] }) => {
    const { data, value, field, options } = props;

    const values = Array.isArray(value) ? value : [value];

    // Extract IDs for the selected values
    const ids = (options || [])
      .filter(({ name }) => values.includes(name!))
      .map(({ id }) => id?.toString());

    const updateFields = cloneDeep(data);

    let updated = false;
    updateFields.forEach((item) => {
      // Check the field through enum
      const enumName = (enumList || []).find((en) => en.id === parseInt(item.key))?.name;
      const isIncluded = [enumName, item.key].includes(field.name);

      if (isIncluded) {
        item.value = ids.join(',');
        updated = true;
      }
    });

    // If no matching entry was updated, push a new object
    if (!updated) {
      updateFields.push({
        key: field.name,
        value: ids.join(',')
      });
    }

    return updateFields;
  }, [enumList]);

  const renderInput = useCallback((field: CustomFields, index: number, isMultiLine: boolean) => {    
    const hasConditionalField = 
    field.mandatoryLevel === MANDATORY_LEVEL.Mandatory || 
    (field.mandatoryLevel === MANDATORY_LEVEL.Conditional && hasConditionalFieldNotEmpty(fields));
    
    const commonProps = {
      label: field.name,
      required: hasConditionalField,
      optionalText: !hasConditionalField,
      value: field.value?.toString(),
      placeholder: (isMultiLine && field.maxLength) ? messages({ id: 'CUSTOM_FIELD_MAX_CHARACTERS' }, { length: field.maxLength }) : field.name,
      maxLength: field.maxLength ?? undefined,
      error: hasError(field),
      caption: hasError(field) ? `${field.name} ${messages({ id: 'MEMBER_FIELD_MANDATORY' })}` : '',
      onChange: (e: { target: { value: string | null | undefined } }) => {
        const value = e.target.value;
      
        const updateData = (data: ApiItemData[], key: string, value: string | null | undefined) => {
          const existingItem = data.find((item) => item.key === key);
          if (existingItem) existingItem.value = value;
          else data.push({ key, value });
        };
      
        if (setData) {
          const updatedData = cloneDeep(data || []);
          updateData(updatedData, field.name, value);
          setData(updatedData);
        }
      
        if (setOrgData && orgData) {
          const updatedOrgData = cloneDeep(orgData);
          updateData(updatedOrgData, field.name, value);
          setOrgData(updatedOrgData);
        }
      
        if (setPersonalDetails && identity) {
          const updatedIdentity = cloneDeep(identity);
          updatedIdentity.data = updatedIdentity.data || [];
          updateData(updatedIdentity.data, field.name, value);
          setPersonalDetails(updatedIdentity);
        }
      },
      onBlur: () => {
        if (!hasOnBlur || hasOnBlur[field.name] === undefined) return;
        setHasOnBlur &&
          setHasOnBlur((prevHasOnBlur) => ({
            ...prevHasOnBlur,
            [field.name]: true,
          }));
      },
    };

    // Multi Line String field
    if (isMultiLine) {
      return (
        <AgTextArea key={index} {...commonProps} style={{ minHeight: '64px', resize: 'vertical' }} />
      );
    } 

    // Normal String field 
    else {
      const isNumericType = [CustomFieldColumnType.Integer, CustomFieldColumnType.Decimal].includes(field.fieldType!);
      const inputType = isNumericType ? 'number' : 'text';
      const onKeyDown = isNumericType ? preventInputLetterE : undefined;

      const unitRight = isNumericType && units.find(({ id }) => id === field.unitId)?.name;

      return (
        <AgTextField key={index} {...commonProps} type={inputType} unitRight={unitRight || ''} onKeyDown={onKeyDown} />
      );
    }
  }, [data, fields, hasError, hasOnBlur, identity, messages, orgData, setData, setHasOnBlur, setOrgData, setPersonalDetails, units]);

  const renderDatePicker = (field: CustomFields, index: number, isDateTime: boolean) => {
    const format = isDateTime ? 'YYYY-MM-DDTHH:mm:ss' : 'YYYY-MM-DD';
    const commonProps = {
      withTime: isDateTime, // Show Date time picker
      label: field.name,
      required:
        field.mandatoryLevel === MANDATORY_LEVEL.Mandatory ||
        (field.mandatoryLevel === MANDATORY_LEVEL.Conditional &&
          hasConditionalFieldNotEmpty(fields)),
      optionalText: !(
        field.mandatoryLevel === MANDATORY_LEVEL.Mandatory ||
        (field.mandatoryLevel === MANDATORY_LEVEL.Conditional &&
          hasConditionalFieldNotEmpty(fields))
      ),
      value: field.value ? dayjs(dayjs(field.value, format)) : undefined,
      error: hasError(field),
      caption: hasError(field)
        ? `${field.name} ${messages({ id: 'MEMBER_FIELD_MANDATORY' })}`
        : '',
      onChange: (e: string | number | Date | dayjs.Dayjs | null | undefined) => {
        const value = e ? dayjs(e).format(format) : '';
        
        const updateData = (data: any[], key: string, value: string) => {
          const existingItem = data.find((item) => item.key === key);
          if (existingItem) existingItem.value = value;
          else data.push({ key, value });
        };
        
        if (setData) {
          const updatedData = cloneDeep(data || []);
          updateData(updatedData, field.name, value);
          setData(updatedData);
        }
        
        if (setOrgData && orgData) {
          const updatedOrgData = cloneDeep(orgData);
          updateData(updatedOrgData, field.name, value);
          setOrgData(updatedOrgData);
        }
        
        if (setPersonalDetails && identity) {
          const updatedIdentity = cloneDeep(identity);
          updatedIdentity.data = updatedIdentity.data || [];
          updateData(updatedIdentity.data, field.name, value);
          setPersonalDetails(updatedIdentity);
        }
      },
      onClose: () => {
        if (!hasOnBlur || hasOnBlur[field.name] === undefined) return;
        setHasOnBlur &&
          setHasOnBlur((prevHasOnBlur) => ({
            ...prevHasOnBlur,
            [field.name]: true,
          }));
      }
    };

    return <AgDatePicker key={index} {...commonProps} />;
  };

  const renderDropdown = (field: CustomFields, index: number, multiple: boolean) => {
    const splittedValues = field?.value ? field.value.toString().split(',') : undefined;
    const enumType = enumList?.find((item) => item.id === Number(field.name));
    const options = enumType?.values?.map((item) => ({ name: item.name ?? '', id: item.id ?? -1 })) || [];
    return (
      <AgDropdown
        disableClearable={!multiple && field.mandatoryLevel === MANDATORY_LEVEL.Mandatory}
        options={options}
        key={index}
        inputLabel={enumType?.name || ''}
        multiple={multiple}
        optional={
          field.mandatoryLevel !== MANDATORY_LEVEL.Mandatory ||
          (field.mandatoryLevel === MANDATORY_LEVEL.Conditional &&
            !hasConditionalFieldNotEmpty(fields))
        }
        value={splittedValues}
        values={splittedValues}
        sx={{ width: 'auto' }}
        error={hasError(field)}
        caption={
          hasError(field)
            ? `${field.name} ${messages({ id: 'MEMBER_FIELD_MANDATORY' })}`
            : ''
        }
        onChange={(value: string) => {
          const commonProps = { value, field, options: options || [] };
          if (setData) {
            const updatedData = handleEnumORMultiEnum({ data: data || [], ...commonProps });
            setData(updatedData);
          };
          if (setOrgData && orgData) {
            const updatedData = handleEnumORMultiEnum({ data: orgData, ...commonProps });
            setOrgData(updatedData);
          };
          if (setPersonalDetails && identity) {
            const updatedData = handleEnumORMultiEnum({ data: identity.data, ...commonProps });
            setPersonalDetails({ ...identity, data: updatedData });
          };
        }}
        onBlur={() => {
          if (!hasOnBlur || hasOnBlur[field.name] === undefined) return;
          setHasOnBlur &&
            setHasOnBlur((prevHasOnBlur) => ({
              ...prevHasOnBlur,
              [field.name]: true
            }));
        }}
      />
    );
  };

  const editOnlyFields = () => {
    return (
      <AgTypography
        sx={{
          paddingTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        {fields.map((field, index) => {
          const { fieldType } = field;

          // Date and DateTime fields
          const isDateField = [CustomFieldColumnType.Date, CustomFieldColumnType.DateTime].includes(fieldType!);
          if (isDateField) return renderDatePicker(field, index, fieldType === CustomFieldColumnType.DateTime);

          // Enum and MultiEnum fields
          const isEnumField = [CustomFieldColumnType.Enum, CustomFieldColumnType.MultiEnum].includes(fieldType!);
          if (isEnumField) return renderDropdown(field, index, fieldType === CustomFieldColumnType.MultiEnum);

          // String, Numeric, MultiLineString fields
          return renderInput(field, index, fieldType === CustomFieldColumnType.MultiLineString);
        })}
      </AgTypography>
    );
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if(setErrors){
        const errors: Record<string, boolean> = {};
        fields.forEach((field) => errors[field.name] = hasError(field, true) || false);
        setErrors(errors);
      }
    });
    return () => clearTimeout(debounce);
  }, [fields, hasError, setErrors]);

  return (
    <AgTypography
      sx={{
        width: mainWidth ?? 'auto',
        padding: expand ? '12px 16px 16px 16px' : '12px 16px',
        background: AgColor.grey2,
        color: AgColor.primaryLight,
        fontWeight: '500',
        borderRadius: '8px',
        border: '0.75px solid #EBEBEB',
        textAlign: 'left',
        boxShadow: '0px 1px 2px 0px #0000000D'
      }}
    >
      <AgTypography
        style={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          style={{
            maxWidth: '95%',
            color: AgColor.primaryLight
          }}
        >
          {title}
        </AgTypography>
        <AgTypography
          onClick={() =>
            setExpandMain ? setExpandMain(!expandMain) : setExpand(!expand)
          }
          sx={{
            cursor: 'pointer',
            height: '20px',
            width: '20px'
          }}
        >
          {expandTmp ? (
            <ExpandLess sx={{ height: '20px' }} />
          ) : (
            <ExpandMore sx={{ height: '20px' }} />
          )}
        </AgTypography>
      </AgTypography>
      {expandTmp ? (
        <AgTypography>
          {editOnly ? editOnlyFields() : readOnlyFields()}
        </AgTypography>
      ) : null}
    </AgTypography>
  );
};

export default AgCustomFieldGroup;
